import { Button } from '@app/components/button/button';
import { useUserAcceptTermsMutation } from '@app/graphql';
import { useAppDispatch, useAppSelector } from '@app/store';
import {
  selectUserHasAcceptedTerms,
  selectUserProfileStatus,
} from '@app/store/selectors';
import { Dialog } from '@headlessui/react';
import { Trans, useTranslation } from '@hopin-team/provider-translation';
import React, { useState } from 'react';

import { CheckmarkNewIcon } from '../icons/checkmark-new';

export const TermsModal: React.FC = () => {
  const hasAcceptedTerms = useAppSelector(selectUserHasAcceptedTerms);
  const userProfileLoadingStatus = useAppSelector(selectUserProfileStatus);
  const dispatch = useAppDispatch();

  const [_acceptTermsResponseData, userAcceptTerms] =
    useUserAcceptTermsMutation();
  const { t } = useTranslation();
  const [modalShowing, setModalShowing] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptTerms(e.target.checked);
  };

  const handleContinue = () => {
    userAcceptTerms({ input: {} });
    setModalShowing(false);
    dispatch({ type: '[nav-bar] user-profile terms accepted' });
  };

  if (userProfileLoadingStatus !== 'success' || hasAcceptedTerms) {
    return null;
  }

  // No operation - user must accept terms
  // And Dialog component requires onClose prop
  const noop = () => undefined;

  return (
    <Dialog open={modalShowing} onClose={noop}>
      <div className="fixed inset-0 bg-overlay backdrop-blur-sm z-[1060] flex items-center justify-center" />
      <Dialog.Panel
        data-testid="terms-modal"
        className="bg-white rounded-lg shadow-lg w-[30rem] z-[1061] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <div className="flex flex-col">
          <div className="py-5 px-6">
            <Dialog.Title className="text-lg font-medium m-0">
              {t('terms.label')}
            </Dialog.Title>
          </div>

          <div className="px-6 pb-4">
            <div className="flex flex-col">
              <h3 className="text-base font-medium mb-2">{t('terms.title')}</h3>
              <p className="text-sm text-gray-700 mb-4">{t('terms.body')}</p>
              <div className="flex items-start gap-2">
                <div className="relative flex items-center m-auto">
                  <input
                    type="checkbox"
                    id="accept-terms-checkbox"
                    checked={acceptTerms}
                    onChange={handleChange}
                    data-testid="accept-checkbox"
                    className="peer h-4 !w-4 cursor-pointer appearance-none rounded border border-gray-400 bg-white checked:bg-primary checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 transition-colors"
                  />
                  <CheckmarkNewIcon
                    className="absolute left-0.5 top-0.5 h-3 w-3 text-white pointer-events-none opacity-0 peer-checked:opacity-100"
                    aria-hidden="true"
                  />
                </div>
                <label
                  htmlFor="accept-terms-checkbox"
                  className="!leading-5 text-xs text-gray-700 cursor-pointer ml-1 mt-2"
                >
                  <Trans
                    i18nKey="terms.agreeTermsAndConditions"
                    components={{
                      2: (
                        <Button
                          as="a"
                          href="https://www.ringcentral.com/legal/acceptable-use-policy.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          unstyled
                          className="text-xs no-underline"
                        />
                      ),
                      5: (
                        <Button
                          as="a"
                          href="https://www.ringcentral.com/legal/ringcentral-events/global-platform-terms.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          unstyled
                          className="text-xs no-underline"
                        />
                      ),
                      7: (
                        <Button
                          as="a"
                          href="https://www.ringcentral.com/legal/privacy-notice.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          unstyled
                          className="text-xs no-underline"
                        />
                      ),
                    }}
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex py-4 px-6 justify-end border-t border-gray-200">
            <Button
              disabled={!acceptTerms}
              inline
              size="small"
              onClick={handleContinue}
            >
              {t('terms.continue')}
            </Button>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
